import {
  useLazyGetLookupProjectMembersQuery,
  useLazyGetLookupUsersQuery,
} from '@crew/apis/lookup/lookupApis'
import { useTranslation } from '@crew/modules/i18n'
import { useDataSource } from 'hooks/dataSource/useDataSource'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
  isValidLoadOptionsFilter,
  pickupIdsFromLoadOptionsFilter,
} from 'utils/filterExpr'
import {
  useGetEventAttendeesQuery,
  useGetEventQuery,
  useInsertEventAttendeesMutation,
} from '@crew/apis/project/projectApis'
import { ValidateRules } from '@crew/utils/form'
import { skipToken } from '@reduxjs/toolkit/query'
import { EventAttendee } from '@crew/models/domain'
import {
  ObjectEventMessage,
  notifyEventAttendeeEvent,
} from 'features/app/states/appSlice'
import { NotifyEventType } from 'enums/app'
import { useAppDispatch } from 'states/hooks'
import { GetEventRequest } from '@crew/apis/project/models/getEvent/request'
import { EventType } from '@crew/enums/domain'

export type FormValues = {
  userIds: string[]
  needNotification: boolean
}
const formInitialValues: FormValues = {
  userIds: [],
  needNotification: false,
}

export const useEventDetailAttendeesEntryForm = (eventType: EventType) => {
  const { t } = useTranslation()
  const { eventId } = useParams()
  const dispatch = useAppDispatch()

  // react-hook-formの各種データを取得
  const { handleSubmit, control, setError, formState } = useForm<FormValues>({
    criteriaMode: 'all',
    defaultValues: formInitialValues,
  })

  // Get the list of event attendees
  const { data: getEventAttendeesResult } = useGetEventAttendeesQuery(
    eventId ? { eventId } : skipToken
  )

  // イベント詳細の取得
  // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
  const useGetEventParam: GetEventRequest | undefined = eventId
    ? {
        eventId,
      }
    : undefined

  const { data: getEventQueryResult } = useGetEventQuery(
    useGetEventParam ?? skipToken
  )

  // Get function to get project members
  const [lazyGetProjectMembersQuery] = useLazyGetLookupProjectMembersQuery()
  const [lazyGetUsersQuery] = useLazyGetLookupUsersQuery()

  // Get data source for event attendees
  const eventAttendeeDataSource = useDataSource(
    () => ({
      key: 'id',
      load: async (loadOptions) => {
        let excludeUserId = undefined
        //get list current user id of event attendees
        if (getEventAttendeesResult?.eventAttendees) {
          excludeUserId = getEventAttendeesResult.eventAttendees.map(
            (attendee) => attendee.userId
          )
        }

        if (loadOptions.filter) {
          // TODO: 3つ目以降のタグを追加するとfilter付のloadが複数発生する
          // https://break-tmc.atlassian.net/browse/CREW-2207

          // ['id', =, 'some_id'] か [['id', =, 'some_id'], 'or',  ...] 形式のみ許可
          if (!isValidLoadOptionsFilter(loadOptions.filter)) {
            return []
          }

          // 選択したユーザIDのフィルタリング
          const filteringUserIds = pickupIdsFromLoadOptionsFilter(
            loadOptions.filter
          )

          if (eventType === EventType.Project) {
            const response = await lazyGetProjectMembersQuery({
              projectId: getEventQueryResult?.event?.entityRecordId as string,
              keyword: undefined,
              userId: filteringUserIds,
              excludeUserId,
              isEnabledUser: true,
            }).unwrap()

            return response.projectMembers
          } else {
            const response = await lazyGetUsersQuery({
              keyword: undefined,
              userId: filteringUserIds,
              limit: undefined,
              excludeExternalUser: true,
            }).unwrap()

            return response.users
          }
        } else {
          // インクリメンタルサーチ
          if (eventType === EventType.Project) {
            const response = await lazyGetProjectMembersQuery({
              projectId: getEventQueryResult?.event?.entityRecordId as string,
              keyword: loadOptions.searchValue,
              userId: undefined,
              excludeUserId,
              isEnabledUser: true,
            }).unwrap()

            return response.projectMembers
          } else {
            const response = await lazyGetUsersQuery({
              keyword: loadOptions.searchValue,
              userId: undefined,
              limit: undefined,
              excludeExternalUser: true,
            }).unwrap()

            return response.users
          }
        }
      },
    }),
    [
      getEventAttendeesResult?.eventAttendees,
      eventType,
      lazyGetProjectMembersQuery,
      getEventQueryResult?.event?.entityRecordId,
      lazyGetUsersQuery,
    ]
  )

  // Get function for insert event attendees
  const [
    insertEventAttendeesMutation,
    { isLoading: isLoadingInsertEventAttendees },
  ] = useInsertEventAttendeesMutation()

  // Call API to insert event attendees
  const insertEventAttendees = useCallback(
    async (eventId: string, data: FormValues) => {
      const payload = {
        eventId,
        userIds: data.userIds,
      }
      await insertEventAttendeesMutation({
        eventAttendees: payload,
        needNotification: data.needNotification,
      }).unwrap()

      //trigger update event attendee list
      const objectEventMessage: ObjectEventMessage<EventAttendee> = {
        eventType: NotifyEventType.Inserted,
        id: payload.eventId,
        object: payload,
      }
      dispatch(notifyEventAttendeeEvent(objectEventMessage))
    },
    [dispatch, insertEventAttendeesMutation]
  )

  //  バリデーションルール
  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      userIds: {
        required: t('message.general.required'),
      },
      needNotification: {},
    }),
    [t]
  )

  return {
    handleSubmit,
    control,
    setError,
    formState,
    validateRules,
    eventAttendeeDataSource,
    insertEventAttendees,
    isLoadingInsertEventAttendees,
  }
}
